/**
 * Модуль для подсчёта статистики боя
 */

/**
 * Вычисляет статистику боя из лога событий
 */
export function calculateBattleStats(battle, panels) {
  if (!battle || !battle.log || !Array.isArray(battle.log)) {
    return {
      totalDamage: 0,
      totalCrits: 0,
      totalAttacks: 0,
      totalMisses: 0,
      totalEvolutions: 0,
      totalStackResets: 0,
      damageByCombatant: new Map(),
      critsByCombatant: new Map(),
      attacksByCombatant: new Map(),
      abilitiesUsed: [],
      rounds: battle?.round || 0
    };
  }

  const stats = {
    totalDamage: 0,
    totalCrits: 0,
    totalAttacks: 0,
    totalMisses: 0,
    totalEvolutions: 0,
    totalStackResets: 0,
    damageByCombatant: new Map(),
    critsByCombatant: new Map(),
    attacksByCombatant: new Map(),
    abilitiesUsed: [],
    rounds: battle.round || 0
  };

  const panelMap = new Map((panels || []).map(p => [String(p.id), p]));

  battle.log.forEach(entry => {
    switch (entry.event) {
      case 'attack':
        stats.totalAttacks++;
        const sourceId = String(entry.source || '');
        const currentAttacks = stats.attacksByCombatant.get(sourceId) || 0;
        stats.attacksByCombatant.set(sourceId, currentAttacks + 1);

        if (entry.value != null && entry.value > 0) {
          const damage = Math.round(entry.value);
          stats.totalDamage += damage;
          
          const currentDamage = stats.damageByCombatant.get(sourceId) || 0;
          stats.damageByCombatant.set(sourceId, currentDamage + damage);

          if (entry.breakdown?.crit) {
            stats.totalCrits++;
            const currentCrits = stats.critsByCombatant.get(sourceId) || 0;
            stats.critsByCombatant.set(sourceId, currentCrits + 1);
          }
        }

        if (entry.abilityType || entry.abilityId) {
          const abilityName = entry.abilityType || entry.abilityId || 'Неизвестная способность';
          const panel = panelMap.get(sourceId);
          const combatantName = panel?.name || `Боец #${sourceId}`;
          stats.abilitiesUsed.push({
            combatantId: sourceId,
            combatantName,
            ability: abilityName,
            round: entry.round || 0
          });
        }
        break;

      case 'miss':
        stats.totalMisses++;
        break;

      case 'evolution':
        stats.totalEvolutions++;
        break;

      case 'stack_reset':
        stats.totalStackResets++;
        break;
    }
  });

  return stats;
}

/**
 * Форматирует статистику для отображения
 */
export function formatBattleStats(stats, panels) {
  const panelMap = new Map((panels || []).map(p => [String(p.id), p]));
  
  const topDamage = Array.from(stats.damageByCombatant.entries())
    .map(([id, damage]) => ({
      id,
      name: panelMap.get(id)?.name || `Боец #${id}`,
      damage
    }))
    .sort((a, b) => b.damage - a.damage)
    .slice(0, 3);

  const topCrits = Array.from(stats.critsByCombatant.entries())
    .map(([id, crits]) => ({
      id,
      name: panelMap.get(id)?.name || `Боец #${id}`,
      crits
    }))
    .sort((a, b) => b.crits - a.crits)
    .slice(0, 3);

  return {
    summary: {
      totalDamage: stats.totalDamage,
      totalCrits: stats.totalCrits,
      totalAttacks: stats.totalAttacks,
      totalMisses: stats.totalMisses,
      totalEvolutions: stats.totalEvolutions,
      totalStackResets: stats.totalStackResets,
      rounds: stats.rounds,
      critRate: stats.totalAttacks > 0 
        ? ((stats.totalCrits / stats.totalAttacks) * 100).toFixed(1) 
        : '0.0',
      avgDamagePerAttack: stats.totalAttacks > 0
        ? (stats.totalDamage / stats.totalAttacks).toFixed(1)
        : '0.0'
    },
    topDamage,
    topCrits,
    abilitiesUsed: stats.abilitiesUsed
  };
}

/**
 * Рендерит статистику боя в HTML
 */
export function renderBattleStats(formattedStats, escapeHtml) {
  const { summary, topDamage, topCrits, abilitiesUsed } = formattedStats;
  
  let html = `
    <div class="battle-stats-summary">
      <h4 style="margin-top: 0; margin-bottom: 12px;">📊 Статистика боя</h4>
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-bottom: 12px;">
        <div class="battle-stat-card">
          <div class="battle-stat-label">Общий урон</div>
          <div class="battle-stat-value">${escapeHtml(String(summary.totalDamage))}</div>
        </div>
        <div class="battle-stat-card">
          <div class="battle-stat-label">Критические</div>
          <div class="battle-stat-value">${escapeHtml(String(summary.totalCrits))}</div>
        </div>
        <div class="battle-stat-card">
          <div class="battle-stat-label">Атак</div>
          <div class="battle-stat-value">${escapeHtml(String(summary.totalAttacks))}</div>
        </div>
        <div class="battle-stat-card">
          <div class="battle-stat-label">Шанс крита</div>
          <div class="battle-stat-value">${escapeHtml(summary.critRate)}%</div>
        </div>
        <div class="battle-stat-card">
          <div class="battle-stat-label">Средний урон</div>
          <div class="battle-stat-value">${escapeHtml(summary.avgDamagePerAttack)}</div>
        </div>
        <div class="battle-stat-card">
          <div class="battle-stat-label">Раундов</div>
          <div class="battle-stat-value">${escapeHtml(String(summary.rounds))}</div>
        </div>
      </div>
  `;

  if (topDamage.length > 0) {
    html += `
      <div style="margin-top: 12px; margin-bottom: 8px;">
        <div style="font-size: 12px; font-weight: 600; color: var(--tg-theme-hint-color); margin-bottom: 6px;">Топ по урону:</div>
        ${topDamage.map((item, index) => `
          <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0;">
            <span>${index + 1}. ${escapeHtml(item.name)}</span>
            <span style="font-weight: 600; color: var(--tg-theme-link-color);">${escapeHtml(String(item.damage))}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  if (topCrits.length > 0) {
    html += `
      <div style="margin-top: 8px; margin-bottom: 8px;">
        <div style="font-size: 12px; font-weight: 600; color: var(--tg-theme-hint-color); margin-bottom: 6px;">Топ по критам:</div>
        ${topCrits.map((item, index) => `
          <div style="display: flex; justify-content: space-between; font-size: 12px; padding: 4px 0;">
            <span>${index + 1}. ${escapeHtml(item.name)}</span>
            <span style="font-weight: 600; color: var(--tg-theme-link-color);">${escapeHtml(String(item.crits))}</span>
          </div>
        `).join('')}
      </div>
    `;
  }

  if (abilitiesUsed.length > 0) {
    html += `
      <div style="margin-top: 8px;">
        <div style="font-size: 12px; font-weight: 600; color: var(--tg-theme-hint-color); margin-bottom: 6px;">Использованные способности (${abilitiesUsed.length}):</div>
        <div style="max-height: 120px; overflow-y: auto; font-size: 11px;">
          ${abilitiesUsed.slice(0, 10).map(item => `
            <div style="padding: 3px 0; border-bottom: 1px solid rgba(255,255,255,0.1);">
              <span style="font-weight: 600;">${escapeHtml(item.combatantName)}</span>
              <span style="color: var(--tg-theme-hint-color);"> → </span>
              <span>${escapeHtml(item.ability)}</span>
              ${item.round > 0 ? `<span style="color: var(--tg-theme-hint-color); font-size: 10px;"> (Раунд ${item.round})</span>` : ''}
            </div>
          `).join('')}
          ${abilitiesUsed.length > 10 ? `<div style="padding: 4px 0; font-size: 10px; color: var(--tg-theme-hint-color); text-align: center;">... и ещё ${abilitiesUsed.length - 10}</div>` : ''}
        </div>
      </div>
    `;
  }

  html += `</div>`;
  return html;
}






